import React, { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig, AbsoluteFill, interpolate, spring, Img, staticFile } from 'remotion';
import { generateMousePath } from '../utils/mousePath';
import type { Vector } from '../utils/mousePath';

// 生成鼠标路径的 Hook
const useMousePath = (start: Vector, end: Vector): Vector[] => {
  return useMemo(() => generateMousePath(start, end), [start.x, start.y, end.x, end.y]);
};

// 鼠标 SVG 图标
const MouseCursor: React.FC<{ x: number; y: number; clicking?: boolean }> = ({ x, y, clicking = false }) => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: clicking ? 'translate(-2px, -2px) scale(0.9)' : 'translate(-2px, -2px)',
        pointerEvents: 'none',
        filter: clicking ? 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.5))' : 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))',
      }}
    >
      <path
        d="M5.5 2l10.5 10.5-4.5 1.5 2.5 6.5-2 1-2.5-6.5-3.5 3.5z"
        fill={clicking ? '#333' : '#fff'}
        stroke="#000"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// 打字文字动画
const TypingText: React.FC<{ 
  text: string; 
  progress: number; 
  x: number; 
  y: number;
  visible: boolean;
}> = ({ text, progress, x, y, visible }) => {
  if (!visible) return null;
  
  const displayText = text.slice(0, Math.floor(progress));
  const cursorOpacity = Math.sin(progress * 8) > 0 ? 1 : 0.3;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        color: '#222',
        fontWeight: 400,
        whiteSpace: 'nowrap',
        transform: 'translateY(-50%)',
      }}
    >
      {displayText}
      <span 
        style={{
          opacity: cursorOpacity,
          borderLeft: '2px solid #4e6ef2',
          paddingLeft: '2px',
          marginLeft: displayText.length > 0 ? '2px' : '0',
        }}
      >
        &nbsp;
      </span>
    </div>
  );
};

// 主组件
export const HumanMouseDemo: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  // 关键点位置（基于 1920x1080 真实百度截图）
  const startPoint = { x: 200, y: 200 }; // 起始位置
  const searchBox = { x: 800, y: 400 }; // 百度搜索框位置（真实位置）
  const cursorTextPos = { x: 660, y: 395 }; // 文字显示位置（搜索框内）
  const searchText = "如何用 Remotion 制作视频"; // 搜索文本
  
  // 生成鼠标路径
  const mousePath = useMousePath(startPoint, searchBox);
  
  // 动画阶段
  const moveStartTime = 0; 
  const moveEndTime = 90; // 0-90 帧：鼠标移动到搜索框
  const clickTime = 100; // 100-110 帧：点击搜索框
  const typeStartTime = 130; // 130-280 帧：输入文字
  const typeEndTime = 280;
  
  // 计算当前阶段
  let mousePosition = startPoint;
  let isClicking = false;
  let textProgress = 0;
  let showText = false;
  
  // 阶段 1: 鼠标移动
  if (frame >= moveStartTime && frame < moveEndTime) {
    const easedProgress = spring({
      frame: frame - moveStartTime,
      fps,
      config: {
        damping: 20,
        stiffness: 100,
        mass: 0.5,
      },
    });
    
    // 从路径中获取当前位置
    const pathIndex = Math.min(
      Math.floor(easedProgress * (mousePath.length - 1)),
      mousePath.length - 1
    );
    
    if (mousePath[pathIndex]) {
      mousePosition = mousePath[pathIndex];
    }
  }
  
  // 阶段 2: 匚在搜索框，准备点击
  else if (frame >= moveEndTime && frame < clickTime) {
    mousePosition = searchBox;
  }
  
  // 阶段 3: 点击搜索框
  else if (frame >= clickTime && frame < typeStartTime) {
    mousePosition = searchBox;
    isClicking = frame < clickTime + 10; // 点击持续 10 帧
  }
  
  // 阶段 4: 输入文字
  else if (frame >= typeStartTime) {
    mousePosition = { x: searchBox.x + 150, y: searchBox.y + 20 }; // 光标位置稍微偏移
    showText = true;
    textProgress = interpolate(
      frame,
      [typeStartTime, typeEndTime],
      [0, searchText.length],
      { extrapolateRight: 'clamp' }
    );
  }
  
  return (
    <AbsoluteFill style={{ backgroundColor: '#fff' }}>
      {/* 真实的百度网页截图 */}
      <Img 
        src={staticFile('baidu-screenshot.png')} 
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      
      {/* 输入的文字 */}
      <TypingText
        text={searchText}
        progress={textProgress}
        x={cursorTextPos.x}
        y={cursorTextPos.y}
        visible={showText}
      />
      
      {/* 鼠标光标 */}
      <MouseCursor x={mousePosition.x} y={mousePosition.y} clicking={isClicking} />
    </AbsoluteFill>
  );
};
